import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const linkClass = (isActive?: boolean) =>
    `text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
      scrolled || !isHome
        ? isActive
          ? "text-primary-600"
          : "text-foreground-700 hover:text-foreground-950"
        : "text-white/90 hover:text-white"
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-black/30 backdrop-blur-sm"
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-bold whitespace-nowrap transition-colors duration-300 ${
              scrolled || !isHome ? "text-foreground-950" : "text-white"
            }`}
          >
            ACBuySpreadsheet Hub
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={linkClass(location.pathname === "/")}>
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled || !isHome
                    ? "text-foreground-700 hover:text-foreground-950"
                    : "text-white/90 hover:text-white"
                }`}
              >
                Categories
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-3 w-64 max-h-[420px] overflow-y-auto z-50">
                  <div className="px-4 pb-2 mb-2 border-b border-background-200/50">
                    <p className="text-xs text-foreground-400 font-label uppercase tracking-wider">Product Categories</p>
                  </div>
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 whitespace-nowrap transition-colors duration-150"
                    >
                      <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-right-up-line text-accent-600 text-sm"></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/acbuyspreadsheet-shopping-guide"
              className={linkClass(location.pathname === "/acbuyspreadsheet-shopping-guide")}
            >
              Shopping Guide
            </Link>

            <Link
              to="/what-is-acbuyspreadsheet"
              className={linkClass(location.pathname === "/what-is-acbuyspreadsheet")}
            >
              Guides
            </Link>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={linkClass()}
            >
              Main Site
            </a>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 text-sm font-medium rounded-full border transition-all duration-300 whitespace-nowrap cursor-pointer ${
                scrolled || !isHome
                  ? "border-foreground-300 text-foreground-700 hover:border-foreground-500 hover:text-foreground-950"
                  : "border-white/40 text-white hover:border-white hover:bg-white/10"
              }`}
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer rounded-lg ${
              scrolled || !isHome
                ? "text-foreground-950 hover:bg-background-200"
                : "text-white hover:bg-white/10"
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-colors ${
                  location.pathname === "/"
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                <i className="ri-home-4-line text-lg w-5 h-5 flex items-center justify-center"></i>
                Home
              </Link>

              {/* Mobile Categories */}
              <div>
                <button
                  onClick={() => setMobileCatOpen(!mobileCatOpen)}
                  className="w-full flex items-center justify-between px-3 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <i className="ri-grid-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    Categories
                  </span>
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
                </button>
                {mobileCatOpen && (
                  <div className="ml-3 mt-1 border-l-2 border-accent-200 pl-2 space-y-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      >
                        <span className="w-7 h-7 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-arrow-right-up-line text-accent-600 text-xs"></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/acbuyspreadsheet-shopping-guide"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-colors ${
                  location.pathname === "/acbuyspreadsheet-shopping-guide"
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                <i className="ri-shopping-bag-3-line text-lg w-5 h-5 flex items-center justify-center"></i>
                Shopping Guide
              </Link>

              <Link
                to="/what-is-acbuyspreadsheet"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-colors ${
                  location.pathname === "/what-is-acbuyspreadsheet"
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                <i className="ri-book-open-line text-lg w-5 h-5 flex items-center justify-center"></i>
                Guides
              </Link>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              >
                <i className="ri-external-link-line text-lg w-5 h-5 flex items-center justify-center"></i>
                Main Site
              </a>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="mt-3 flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-shopping-cart-2-line"></i>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}